/**
 * RevenueCat Web SDK Service
 * Handles all RevenueCat interactions for web purchases
 */

import { Purchases, type CustomerInfo, type Package, type Offerings } from '@revenuecat/purchases-js';
import { REVENUECAT_CONFIG } from '../config/revenuecatConfig';
import { BACKEND_CONFIG } from '../config/backendConfig';

// Re-export types for use in other modules
export type { Package, CustomerInfo, Offerings };

let purchasesInstance: Purchases | null = null;
let isConfigured = false;

/**
 * Configure RevenueCat with the user's family ID as the app user ID
 * Should be called once when user logs in or on app initialization
 * Also syncs email attribute to backend for management URL to work
 */
export const configureRevenueCat = async (familyId: string): Promise<Purchases> => {
  if (isConfigured && purchasesInstance) {
    console.log('[RevenueCat] Already configured, returning existing instance');
    return purchasesInstance;
  }

  console.log('[RevenueCat] Configuring with familyId:', familyId);
  
  purchasesInstance = Purchases.configure({
    apiKey: REVENUECAT_CONFIG.apiKey,
    appUserId: familyId,
  });
  
  isConfigured = true;
  
  // Sync email attribute to RevenueCat for management URL to work
  try {
    await syncSubscriberAttributes();
    console.log('[RevenueCat] Email attribute synced');
  } catch (error) {
    console.warn('[RevenueCat] Failed to sync email attribute:', error);
    // Don't fail configuration if attribute sync fails
  }
  
  return purchasesInstance;
};

/**
 * Sync subscriber attributes (email) with RevenueCat
 * This ensures the management URL placeholder {{email}} works
 */
export const syncSubscriberAttributes = async (): Promise<void> => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(`${BACKEND_CONFIG.baseURL}/revenuecat/sync-attributes`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message || 'Failed to sync attributes');
  }
};

/**
 * Get the configured Purchases instance
 * Throws if not configured
 */
export const getPurchases = (): Purchases => {
  if (!purchasesInstance || !isConfigured) {
    throw new Error('RevenueCat not configured. Call configureRevenueCat() first.');
  }
  return purchasesInstance;
};

/**
 * Check if RevenueCat is configured
 */
export const isRevenueCatConfigured = (): boolean => {
  return isConfigured && purchasesInstance !== null;
};

/**
 * Get customer info (subscription status, entitlements)
 * Uses cached data by default for performance
 */
export const getCustomerInfo = async (): Promise<CustomerInfo> => {
  const purchases = getPurchases();
  return await purchases.getCustomerInfo();
};

/**
 * Force refresh customer info from RevenueCat servers
 * Use this after backend operations that change entitlements (like promo code redemption)
 */
export const refreshCustomerInfo = async (): Promise<CustomerInfo> => {
  const purchases = getPurchases();
  return await purchases.getCustomerInfo();
};

/**
 * Get available offerings (plans/packages)
 */
export const getOfferings = async (): Promise<Offerings> => {
  const purchases = getPurchases();
  return await purchases.getOfferings();
};

/**
 * Purchase a package
 * Can render in an embedded element via htmlTarget or redirect to Stripe checkout
 * @param pkg - The RevenueCat package to purchase
 * @param customerEmail - Pre-fill email for checkout
 * @param htmlTarget - Element to render embedded checkout
 */
export const purchasePackage = async (
  pkg: Package,
  customerEmail?: string,
  htmlTarget?: HTMLElement
): Promise<{ customerInfo: CustomerInfo }> => {
  const purchases = getPurchases();
  
  const purchaseParams: any = {
    rcPackage: pkg,
  };
  
  // If we have the customer's email, skip the email collection step
  if (customerEmail) {
    purchaseParams.customerEmail = customerEmail;
  }
  
  // If htmlTarget is provided, render checkout embedded in that element
  if (htmlTarget) {
    purchaseParams.htmlTarget = htmlTarget;
  }
  
  return await purchases.purchase(purchaseParams);
};

/**
 * Check if user has an active entitlement
 */
export const hasEntitlement = async (entitlementId: string): Promise<boolean> => {
  try {
    const customerInfo = await getCustomerInfo();
    return entitlementId in customerInfo.entitlements.active;
  } catch (error) {
    console.error('[RevenueCat] Error checking entitlement:', error);
    return false;
  }
};

/**
 * Check if user has regular access
 */
export const hasRegularAccess = async (): Promise<boolean> => {
  return await hasEntitlement(REVENUECAT_CONFIG.entitlements.regular);
};

/**
 * Get the current subscription tier. 'premium' is returned only for a legacy
 * premium_access grant (Premium retired 2026-09-11) — it still means active
 * access, but never render it as a product.
 */
export const getCurrentTier = async (): Promise<'none' | 'regular' | 'premium'> => {
  try {
    const customerInfo = await getCustomerInfo();
    const activeEntitlements = customerInfo.entitlements.active;
    
    if (REVENUECAT_CONFIG.entitlements.premium in activeEntitlements) {
      return 'premium';
    }
    if (REVENUECAT_CONFIG.entitlements.regular in activeEntitlements) {
      return 'regular';
    }
    return 'none';
  } catch (error) {
    console.error('[RevenueCat] Error getting current tier:', error);
    return 'none';
  }
};

/**
 * Get subscription details from customer info
 */
export const getSubscriptionDetails = async (): Promise<{
  isActive: boolean;
  tier: 'none' | 'regular' | 'premium';
  expiresAt: string | null;
  willRenew: boolean;
  store: string | null;
  productId: string | null;
}> => {
  try {
    const customerInfo = await getCustomerInfo();
    const activeEntitlements = customerInfo.entitlements.active;
    
    // Check premium first (higher tier)
    let tier: 'none' | 'regular' | 'premium' = 'none';
    let entitlement = null;
    
    if (REVENUECAT_CONFIG.entitlements.premium in activeEntitlements) {
      tier = 'premium';
      entitlement = activeEntitlements[REVENUECAT_CONFIG.entitlements.premium];
    } else if (REVENUECAT_CONFIG.entitlements.regular in activeEntitlements) {
      tier = 'regular';
      entitlement = activeEntitlements[REVENUECAT_CONFIG.entitlements.regular];
    }
    
    if (!entitlement) {
      return {
        isActive: false,
        tier: 'none',
        expiresAt: null,
        willRenew: false,
        store: null,
        productId: null,
      };
    }

    // Determine store - promotional entitlements may not have a store
    // or have product_identifier set to the entitlement ID itself
    let store = entitlement.store || null;
    const productId = entitlement.productIdentifier || null;
    
    // Check if this is a promotional grant
    // Promotional entitlements have product_identifier matching the entitlement ID
    // (e.g., 'regular_access' or 'premium_access') or no store
    if (!store || productId === 'regular_access' || productId === 'premium_access') {
      store = 'promotional';
    }
    
    return {
      isActive: true,
      tier,
      expiresAt: entitlement.expirationDate ? entitlement.expirationDate.toISOString() : null,
      willRenew: store === 'promotional' ? false : (entitlement.willRenew || false),
      store,
      productId,
    };
  } catch (error) {
    console.error('[RevenueCat] Error getting subscription details:', error);
    return {
      isActive: false,
      tier: 'none',
      expiresAt: null,
      willRenew: false,
      store: null,
      productId: null,
    };
  }
};

/**
 * Reset RevenueCat configuration (call on logout)
 */
export const resetRevenueCat = (): void => {
  purchasesInstance = null;
  isConfigured = false;
  console.log('[RevenueCat] Reset configuration');
};

/**
 * Prepare for a purchase by calling the backend to revoke any promotional entitlements
 * and check for cross-store conflicts.
 * This prevents the "dual entitlement" bug when upgrading from promo to paid.
 * Should be called BEFORE initiating a purchase via purchasePackage()
 *
 * One plan (Premium retired 2026-09-11): the only purchasable target is the
 * APS Membership ('regular'). A team-covered family is answered 409 with
 * code/error 'TEAM_COVERED' (+ rcSynced, and managementUrl when it also pays
 * through a live store) — callers treat that as information, not a failure.
 */
export const preparePurchase = async (
  targetPlan: 'regular'
): Promise<{
  success: boolean;
  action: string;
  message: string;
  error?: string;
  code?: string;
  rcSynced?: boolean;
  currentStore?: string;
  currentTier?: string;
  managementUrl?: string;
  instructions?: string;
}> => {
  // Try sessionStorage first (primary), then localStorage as fallback
  const token = sessionStorage.getItem('sessionToken') || localStorage.getItem('sessionToken') || localStorage.getItem('token');
  if (!token) {
    return {
      success: false,
      action: 'error',
      message: 'Not authenticated',
      error: 'NO_AUTH',
    };
  }

  try {
    const response = await fetch(`${BACKEND_CONFIG.baseURL}/revenuecat/prepare-purchase`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      // Web purchases are always through Stripe (RC Web Billing)
      body: JSON.stringify({ targetPlan, purchaseStore: 'stripe' }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        action: 'error',
        message: data.message || 'Failed to prepare purchase',
        error: data.error,
        code: data.code,
        rcSynced: data.rcSynced,
        currentStore: data.currentStore,
        currentTier: data.currentTier,
        managementUrl: data.managementUrl,
        instructions: data.instructions,
      };
    }

    return {
      success: true,
      action: data.action,
      message: data.message,
    };
  } catch (error) {
    console.error('[RevenueCat] Error preparing purchase:', error);
    return {
      success: false,
      action: 'error',
      message: 'Network error preparing purchase',
      error: 'NETWORK_ERROR',
    };
  }
};

export default {
  configureRevenueCat,
  syncSubscriberAttributes,
  getPurchases,
  isRevenueCatConfigured,
  getCustomerInfo,
  refreshCustomerInfo,
  getOfferings,
  purchasePackage,
  preparePurchase,
  hasEntitlement,
  hasRegularAccess,
  getCurrentTier,
  getSubscriptionDetails,
  resetRevenueCat,
};
